import { html, View } from 'rune-ts';
import klass from './RecentlySoldProductSection.module.scss';
import {
  ProductCardView,
  ProductLikeToggleEvent,
} from '../../../../components/cells/ProductCard/ProductCardView.ts';
import { ProductCardViewData } from '../../../../../features/ProductList/type';
import { getResizedUrl } from '../../../../../shared/util/image';
import { CreatorConstantS } from '../../../../../features/Creator/outbound/share';
import { SwiperChangeEvent, SwiperView } from '../../../../components/cells/Swiper/Swiper';
import { makeRankScreenNavigate } from '../../../../../shared/app/navigate';
import { SectionTitle } from '@services/MarppleShop/renderApp/components/cells/SectionTitle/SectionTitle.ts';
import { SeeMoreLinkPlugin } from '@services/MarppleShop/renderApp/components/cells/SectionTitle/plugins';
import { createGoodsUrlBuilder } from '@services/MarppleShop/features/ProductList/util';
import {
  CardProductControllerView,
  TCardProductControllerProps,
} from '@services/MarppleShop/shared/components/atoms/MarppleShopDesignSystem/CardProduct/CardProductControllerView.ts';
import { map, pipe, take, toArray } from '@fxts/core';

const MO_SWIPER_OPTION = {
  autoplay: false,
  slideClass: klass.product_card,
  slidesPerView: 'auto',
  speed: 600,
} as const;

export interface RecentlySoldProductSectionData {
  products: ProductCardViewData[];
  banner_images: string[];
}

interface RecentlySoldProductSectionOption {
  is_mobile: boolean;
}

export class RecentlySoldProductSection extends View<RecentlySoldProductSectionData> {
  content_swiper_view: SwiperView<TCardProductControllerProps, typeof CardProductControllerView>;

  private localized_text: ReturnType<typeof this.getLocalizedText>;

  constructor(
    data: RecentlySoldProductSectionData,
    private option: RecentlySoldProductSectionOption,
  ) {
    super(data, option);
    this.localized_text = this.getLocalizedText();

    this.content_swiper_view = new SwiperView(
      { ...MO_SWIPER_OPTION, init: option.is_mobile },
      {
        swiper: klass.product_cards_swiper,
        wrapper: klass.product_cards_container,
      },
      CardProductControllerView,
      this.data.products.map((product) => ({
        card_product_view_props: {
          type: 'B',
          size: 'Small',
          layout: 'Vertical',
          theme: 'Dark',
        },
        product_card_view_data: product,
        options: {
          resize_width: 600,
          klass: klass.product_card,
        },
      })),
    );
  }

  override template({ products }: RecentlySoldProductSectionData) {
    if (!RecentlySoldProductSection.isVisible(this.data)) {
      return html``;
    }

    const { is_mobile } = this.option;
    const see_more_link = createGoodsUrlBuilder().setPage('LATEST_SOLD').build();

    return html`<section class="${klass.section}">
      <img
        class="${klass.background_image}"
        src="${getResizedUrl({
          url: this.getBannerImageByIndex(0),
          quality: 70,
          format: 'webp',
        })}"
        alt=""
      />
      <div class="${klass.blur_backdrop}"></div>
      <div class="${klass.content_wrapper}">
        <div class="${klass.content}">
          <div class="${klass.section_title}">
            ${new SectionTitle({
              title_props: {
                title: this.localized_text.title,
                size: is_mobile ? 'large' : 'xLarge',
                theme: 'dark',
              },
              plugins: [
                new SeeMoreLinkPlugin({
                  navigate: makeRankScreenNavigate(see_more_link),
                  see_more_link,
                  size: is_mobile ? 'xSmall' : 'medium',
                  theme: 'dark',
                }),
              ],
            })}
          </div>
          ${is_mobile
            ? this.content_swiper_view
            : html`<div class="${klass.product_image_list}">
                ${pipe(
                  products,
                  map(
                    (product) =>
                      new CardProductControllerView({
                        card_product_view_props: {
                          type: 'B',
                          size: 'Xlarge',
                          layout: 'Vertical',
                          theme: 'Dark',
                        },
                        product_card_view_data: product,
                        options: {
                          resize_width: 600,
                        },
                      }),
                  ),
                  take(4),
                  toArray,
                )}
              </div>`}
        </div>
      </div>
    </section>`;
  }

  override onRender() {
    this.delegate(ProductLikeToggleEvent, ProductCardView, async (_, view) => {
      const [latest] = this.data.products;
      if (latest && latest.id == view.data.id) {
        await view.toggleLikeButton();
      }
    });

    if (this.option.is_mobile) {
      this.delegate(SwiperChangeEvent, SwiperView, (e) => {
        const { current_index } = e.detail;
        this.setBannerImageToBackground(this.getBannerImageByIndex(current_index));
      });
    }
  }

  private getBannerImageByIndex(index: number): string {
    return this.data.banner_images[index] || CreatorConstantS.banner_img;
  }

  private setBannerImageToBackground(image_url: string) {
    const image_el = this.element().querySelector(`.${klass.background_image}`);
    if (!image_el) {
      return;
    }
    image_el.setAttribute('src', image_url);
  }

  static isVisible(data: RecentlySoldProductSectionData): boolean {
    return data.products.length >= 1;
  }

  private getLocalizedText() {
    return {
      title: this.option.is_mobile ? ET('mps2::main::realtime::title_mo') : ET('mps2::main::realtime::title'),
      sub_title: ET('mps2::main::realtime::sub_title'),
    };
  }
}
